/**
 * Menu.jsx — the overflow ("hamburger") menu.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WHAT WAS WRONG BEFORE
 *
 * The original had four different menu implementations, and every one of them
 * was broken in a different way:
 *
 *   1. `<Hamburger>` rendered a `position: fixed` wrapper with no coordinates,
 *      then a second component inside it that also positioned itself. The outer
 *      div sat at 0,0 — which is why menus appeared stuck to the edge of the
 *      screen instead of next to the button that opened them.
 *
 *   2. The post menu called useRef and useState inside an IIFE inside .map().
 *      That is a Rules of Hooks violation: React matches hooks by call order,
 *      so adding or removing one post silently reassigned every menu's state to
 *      a different post. That is why the wrong menu opened, and why menus
 *      stopped opening at all after a delete.
 *
 *   3. Positioning was done in a `ref={el => {...}}` callback reading
 *      getBoundingClientRect during render. It ran before layout on first
 *      paint, so the first open was always misplaced.
 *
 *   4. z-index was hard-coded to 9999 in some places and 40 in others, while
 *      the sticky header was 20 and cards had their own stacking contexts. A
 *      menu inside a card with `overflow: hidden` was clipped no matter what
 *      z-index it claimed.
 *
 * HOW THIS FIXES IT
 *
 *   * One component, used everywhere.
 *   * Rendered through createPortal into document.body, so no ancestor's
 *     overflow, transform or stacking context can ever clip it.
 *   * Position measured in useLayoutEffect (after layout, before paint), so it
 *     is correct on the very first frame.
 *   * Collision detection: flips above the trigger when there is not enough
 *     room below, and clamps horizontally to the viewport.
 *   * On narrow screens it becomes a bottom sheet, which is what every native
 *     app does and what makes one-handed use on a phone possible.
 *   * Full keyboard support and a focus trap, because a menu you cannot reach
 *     with Tab is not a menu.
 * ═══════════════════════════════════════════════════════════════════════════
 */

import {
  useRef, useState, useEffect, useLayoutEffect, useCallback, useId,
} from 'react';
import { createPortal } from 'react-dom';

const GUTTER = 8;        // minimum distance from any viewport edge
const OFFSET = 6;        // gap between trigger and menu
const MOBILE_BREAKPOINT = 560;

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT
  );
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return isMobile;
}

/**
 * @param {object} props
 * @param {(props: {ref, onClick, 'aria-expanded', 'aria-haspopup', id}) => JSX.Element} props.trigger
 * @param {Array<null | {
 *   id: string, label: string, icon?: JSX.Element, onSelect: () => void,
 *   danger?: boolean, disabled?: boolean, description?: string
 * }>} props.items  — nulls are dropped, so callers can inline conditionals
 * @param {'start'|'end'} [props.align]
 * @param {string} props.label  accessible name for the menu itself
 */
export default function Menu({ trigger, items, align = 'end', label = 'More options' }) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState(null);
  const [activeIndex, setActiveIndex] = useState(-1);

  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const itemRefs = useRef([]);
  const isMobile = useIsMobile();
  const menuId = useId();
  const triggerId = useId();

  const options = (items || []).filter(Boolean);

  /* ── Positioning ────────────────────────────────────────────────────────── */

  const position = useCallback(() => {
    const trig = triggerRef.current;
    const menu = menuRef.current;
    if (!trig || !menu) return;

    const t = trig.getBoundingClientRect();
    const m = menu.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    const spaceBelow = vh - t.bottom;
    const spaceAbove = t.top;
    // Flip up only if there genuinely is not room below AND there is more room
    // above — flipping into an even smaller space helps nobody.
    const flipUp = spaceBelow < m.height + OFFSET + GUTTER && spaceAbove > spaceBelow;

    let top = flipUp ? t.top - m.height - OFFSET : t.bottom + OFFSET;
    top = Math.max(GUTTER, Math.min(top, vh - m.height - GUTTER));

    let left = align === 'end' ? t.right - m.width : t.left;
    left = Math.max(GUTTER, Math.min(left, vw - m.width - GUTTER));

    setCoords({ top, left, maxHeight: vh - GUTTER * 2 });
  }, [align]);

  // useLayoutEffect, not useEffect: this runs after the DOM is measured but
  // before the browser paints, so the menu is never visible in the wrong place.
  useLayoutEffect(() => {
    if (open && !isMobile) position();
  }, [open, isMobile, position]);

  /* ── Dismissal ──────────────────────────────────────────────────────────── */

  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (e) => {
      if (menuRef.current?.contains(e.target)) return;
      if (triggerRef.current?.contains(e.target)) return;
      setOpen(false);
    };

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setOpen(false);
        triggerRef.current?.focus();
      }
    };

    // Closing on scroll rather than repositioning is deliberate: a menu that
    // chases the page while you scroll feels broken, and every native app
    // dismisses instead.
    const onScroll = () => setOpen(false);

    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);

    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown, true);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open]);

  /* ── Body scroll lock for the mobile sheet ──────────────────────────────── */

  useEffect(() => {
    if (!open || !isMobile) return undefined;
    const { overflow, paddingRight } = document.body.style;
    // Compensating for the scrollbar prevents the layout shifting sideways when
    // it is hidden — a small thing that reads as sloppiness when it happens.
    const barWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    if (barWidth > 0) document.body.style.paddingRight = `${barWidth}px`;
    return () => {
      document.body.style.overflow = overflow;
      document.body.style.paddingRight = paddingRight;
    };
  }, [open, isMobile]);

  /* ── Keyboard navigation ────────────────────────────────────────────────── */

  useEffect(() => {
    if (open) {
      setActiveIndex(-1);
      // Focus the menu container so Escape and arrows work immediately, without
      // stealing the visual highlight from the first item.
      requestAnimationFrame(() => menuRef.current?.focus());
    }
  }, [open]);

  const move = useCallback((delta) => {
    const enabled = options
      .map((o, i) => (o.disabled ? -1 : i))
      .filter((i) => i >= 0);
    if (!enabled.length) return;

    const currentPos = enabled.indexOf(activeIndex);
    const nextPos = currentPos === -1
      ? (delta > 0 ? 0 : enabled.length - 1)
      : (currentPos + delta + enabled.length) % enabled.length;

    const next = enabled[nextPos];
    setActiveIndex(next);
    itemRefs.current[next]?.focus();
  }, [options, activeIndex]);

  const onMenuKeyDown = (e) => {
    switch (e.key) {
      case 'ArrowDown': e.preventDefault(); move(1); break;
      case 'ArrowUp':   e.preventDefault(); move(-1); break;
      case 'Home':      e.preventDefault(); setActiveIndex(0); itemRefs.current[0]?.focus(); break;
      case 'End': {
        e.preventDefault();
        const last = options.length - 1;
        setActiveIndex(last);
        itemRefs.current[last]?.focus();
        break;
      }
      case 'Tab':
        // Tabbing out of a menu should close it, not move focus behind it.
        setOpen(false);
        break;
      default:
        break;
    }
  };

  const select = (item) => {
    if (item.disabled) return;
    setOpen(false);
    triggerRef.current?.focus();
    // Defer so the menu is unmounted before the action runs — otherwise an
    // action that opens a modal fights the menu's focus restoration.
    requestAnimationFrame(() => item.onSelect?.());
  };

  /* ── Render ─────────────────────────────────────────────────────────────── */

  const triggerEl = trigger({
    ref: triggerRef,
    id: triggerId,
    onClick: (e) => { e.stopPropagation(); setOpen((o) => !o); },
    'aria-haspopup': 'menu',
    'aria-expanded': open,
    'aria-controls': open ? menuId : undefined,
  });

  const list = (
    <div
      ref={menuRef}
      id={menuId}
      role="menu"
      aria-label={label}
      aria-labelledby={triggerId}
      tabIndex={-1}
      onKeyDown={onMenuKeyDown}
      className={isMobile ? 'menu menu--sheet' : 'menu menu--popover'}
      style={
        isMobile
          ? undefined
          : {
              top: coords?.top ?? -9999,
              left: coords?.left ?? -9999,
              maxHeight: coords?.maxHeight,
              // Hidden until measured, so it never flashes at 0,0.
              visibility: coords ? 'visible' : 'hidden',
            }
      }
    >
      {isMobile && <div className="menu__grabber" aria-hidden="true" />}

      {options.map((item, i) => (
        <button
          key={item.id}
          ref={(el) => { itemRefs.current[i] = el; }}
          type="button"
          role="menuitem"
          disabled={item.disabled}
          tabIndex={-1}
          className={`menu__item${item.danger ? ' menu__item--danger' : ''}`}
          onClick={() => select(item)}
          onMouseEnter={() => setActiveIndex(i)}
        >
          {item.icon && <span className="menu__icon" aria-hidden="true">{item.icon}</span>}
          <span className="menu__labels">
            <span className="menu__label">{item.label}</span>
            {item.description && (
              <span className="menu__description">{item.description}</span>
            )}
          </span>
        </button>
      ))}
    </div>
  );

  return (
    <>
      {triggerEl}
      {open && createPortal(
        isMobile ? (
          <div className="menu__backdrop" onClick={() => setOpen(false)}>
            <div onClick={(e) => e.stopPropagation()}>{list}</div>
          </div>
        ) : (
          list
        ),
        document.body
      )}
    </>
  );
}

/**
 * Styles live next to the component rather than in a global sheet so that the
 * z-index, the portal target and the layout can never drift apart again.
 */
export const MENU_CSS = `
.menu {
  background: var(--card);
  border: 1px solid var(--line-strong);
  border-radius: var(--radius);
  box-shadow: var(--shadow-lg);
  padding: 6px;
  min-width: 220px;
  overflow-y: auto;
  overscroll-behavior: contain;
  z-index: var(--z-dropdown);
}
.menu:focus { outline: none; }

.menu--popover {
  position: fixed;
  animation: pop-in 120ms ease both;
}

.menu__backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: flex-end;
  z-index: var(--z-overlay);
  animation: fade-in-up 140ms ease both;
}

.menu--sheet {
  width: 100vw;
  border-radius: var(--radius-lg) var(--radius-lg) 0 0;
  border-bottom: none;
  padding: 8px 8px calc(8px + env(safe-area-inset-bottom, 0px));
  max-height: 70vh;
  animation: slide-up 200ms cubic-bezier(0.32, 0.72, 0, 1) both;
}

.menu__grabber {
  width: 36px; height: 4px;
  background: var(--line-strong);
  border-radius: var(--radius-full);
  margin: 4px auto 10px;
}

.menu__item {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  /* Meets the 44px minimum target size on touch without looking oversized on desktop. */
  min-height: var(--tap);
  padding: 10px 12px;
  background: transparent;
  border: none;
  border-radius: var(--radius-sm);
  color: var(--ink);
  font: inherit;
  font-size: var(--text-sm);
  text-align: left;
  cursor: pointer;
  transition: background var(--transition);
}
.menu__item:hover:not(:disabled),
.menu__item:focus-visible { background: var(--bg-alt); }
.menu__item:focus-visible { box-shadow: var(--focus-ring); }
.menu__item:disabled { opacity: 0.45; cursor: not-allowed; }
.menu__item--danger { color: var(--danger); }

.menu__icon { display: inline-flex; flex-shrink: 0; }
.menu__labels { display: flex; flex-direction: column; gap: 1px; min-width: 0; }
.menu__label { font-weight: 500; }
.menu__description {
  font-size: var(--text-xs);
  color: var(--ink-faint);
  line-height: 1.35;
}
`;
